import { useMemo } from "react";
import { Info } from "lucide-react";
import type { MappingSchema } from "./customFieldMappingHelpers";
import { SelectOptionMappingRows } from "./SelectOptionMappingRows";
import { getQuotedByMappingOptions } from "./quotedByMappingHelpers";

interface QuotedByMappingSectionProps {
  /** Current user id to employee code mapping from the form config. */
  mapping: Record<string, string>;
  /** Called when a user's employee mapping changes. */
  onMappingChange: (mapping: Record<string, string>) => void;
  /** The resolved `quotedByEmployeeByUserId` schema listing users and employees. */
  mappingSchema: MappingSchema;
}

/**
 * Pairs each Forgepoint user with their JobBOSS² employee, written as Quoted By
 * for estimators and as Account Manager for sales reps.
 * @param props - QuotedByMappingSectionProps
 * @returns The mapping section element, or null when there are no users
 */
export function QuotedByMappingSection({
  mapping,
  onMappingChange,
  mappingSchema,
}: QuotedByMappingSectionProps) {
  const { userIds, userLabels, employeeOptions } = useMemo(
    () => getQuotedByMappingOptions(mappingSchema),
    [mappingSchema],
  );

  if (userIds.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 border-t pt-5">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Employee Mapping
      </span>
      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Quoted By is the first estimator on the quote&apos;s opportunity.
          Account Manager is the quote&apos;s sales rep.
        </span>
      </p>
      <SelectOptionMappingRows
        title="Map users to employees"
        fpOptions={userIds}
        fpOptionLabels={userLabels}
        erpOptions={employeeOptions}
        optionMapping={mapping}
        onOptionMappingChange={onMappingChange}
        searchable
        showAutoMap={false}
        unmappedHint="Unmapped users leave their JobBOSS² field empty."
      />
    </div>
  );
}
